import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SBookingCard from './SBookingCard';
import { BookingDetailsDto } from '@/services/booking.service';
import { useCalendarSponsorships } from '@/hooks/useCalendarSponsorships';
import './BookingCarousel.css';

// Reschedule/cancel stay available for the entire pre-start window — even
// while Check-in is already showing (it opens 30 min early) — and only
// disappear once the booking's actual start time has passed.
function hasBookingStarted(booking: BookingDetailsDto): boolean {
  return new Date(booking.startTime).getTime() <= Date.now();
}

interface BookingCarouselProps {
  bookings: BookingDetailsDto[];
  onCancel?: (bookingId: string) => void;
  onReschedule?: (bookingId: string) => void;
  onCheckIn?: (bookingId: string) => void;
  onViewSession?: (bookingId: string) => void;
  onVisitShop?: () => void;
  onOpenGate?: (bookingId: string) => void;
  cancellingBookingId?: string | null;
  isCheckingIn?: boolean;
  isRefreshing?: boolean;
}

const variants = {
  enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
};

export const BookingCarousel: React.FC<BookingCarouselProps> = ({
  bookings,
  onCancel,
  onReschedule,
  onCheckIn,
  onViewSession,
  onVisitShop,
  onOpenGate,
  cancellingBookingId = null,
  isCheckingIn = false,
  isRefreshing = false,
}) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  // Show max 4 bookings
  const maxBookings = 4;
  const displayBookings = bookings.slice(0, maxBookings);
  const hasMoreBookings = bookings.length > maxBookings;

  // Clamp current index when bookings shrink (e.g. after a cancellation)
  React.useEffect(() => {
    if (current >= displayBookings.length && displayBookings.length > 0) {
      setCurrent(displayBookings.length - 1);
    }
  }, [displayBookings.length]);

  // Batch-fetch sponsorships for all bookings (hook must be called before any early return)
  const calendarEntries = bookings
    .filter((b) => Boolean(b.calendarId))
    .map((b) => ({ calendarId: b.calendarId, locationId: b.locationId }));
  const { byCalendarId } = useCalendarSponsorships(calendarEntries);

  const getSponsorship = (b: BookingDetailsDto) => {
    const resolved = byCalendarId[b.calendarId];
    if (resolved?.isEnabled) return { sponsorName: resolved.sponsorName };
    // Fall back to snapshot for bookings that have already transitioned (Phase 286)
    return b.snapshotSponsorName ? { sponsorName: b.snapshotSponsorName } : null;
  };

  if (bookings.length === 0) return null;

  const activeBooking = displayBookings[current];
  if (!activeBooking) return null;

  const paginate = (newDir: number) => {
    // If swiping right from the last booking and there are more, navigate to all bookings
    if (newDir > 0 && current === displayBookings.length - 1 && hasMoreBookings) {
      navigate('/bookings/all');
      return;
    }
    
    // Prevent swiping beyond bounds
    const nextIndex = current + newDir;
    if (nextIndex < 0 || nextIndex >= displayBookings.length) {
      return;
    }
    
    setDirection(newDir);
    setCurrent(nextIndex);
  };

  const handleDotClick = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };

  return (
    <div className="booking-carousel">
      <div className="booking-carousel-track">
        {/* Invisible sizer — must receive the same props as the animated card
            so it renders at identical height, keeping dots in a fixed position. */}
        {/* Invisible sizer — keeps layout stable while animated card slides */}
        <div className="booking-carousel-sizer" aria-hidden="true">
          {(() => {
            const b = activeBooking;
            const canAct = b.status === 'active' && !hasBookingStarted(b);
            return (
              <SBookingCard
                booking={b}
                sponsorship={getSponsorship(b)}
                onCancel={canAct ? onCancel : undefined}
                onReschedule={canAct ? onReschedule : undefined}
                onCheckIn={onCheckIn}
                onViewSession={onViewSession}
                onVisitShop={onVisitShop}
                onOpenGate={onOpenGate}
                hasBooking={true}
                isCancelling={cancellingBookingId === (b._id || b.id)}
                isCheckingIn={isCheckingIn}
                isRefreshing={isRefreshing}
              />
            );
          })()}
        </div>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            dragDirectionLock={true}
            onDragEnd={(_e, { offset }) => {
              if (offset.x < -60) paginate(1);
              else if (offset.x > 60) paginate(-1);
            }}
            className="booking-carousel-slide"
          >
            {(() => {
              const b = activeBooking;
              const canAct = b.status === 'active' && !hasBookingStarted(b);
              return (
                <SBookingCard
                  booking={b}
                  sponsorship={getSponsorship(b)}
                  onCancel={canAct ? onCancel : undefined}
                  onReschedule={canAct ? onReschedule : undefined}
                  onCheckIn={onCheckIn}
                  onViewSession={onViewSession}
                  onVisitShop={onVisitShop}
                  onOpenGate={onOpenGate}
                  hasBooking={true}
                  isCancelling={cancellingBookingId === (b._id || b.id)}
                  isCheckingIn={isCheckingIn}
                />
              );
            })()}
          </motion.div>
        </AnimatePresence>
      </div>
      {displayBookings.length > 1 && (
        <div className="booking-carousel-dots">
          {displayBookings.map((_, i) => (
            <button
              key={i}
              className={`booking-dot${i === current ? ' active' : ''}`}
              onClick={() => handleDotClick(i)}
              aria-label={`Go to booking ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
